import { Card, IconTile, Spinner, useToast } from "@design";
import { useMutation } from "@tanstack/react-query";
import { FileText, Upload } from "lucide-react";
import { useRef } from "react";
import { useTranslation } from "react-i18next";
import type { BrandExtractionResponse } from "@/api";
import { errorMessage, extractBrandMutation } from "@/api";
import { Text } from "@/components/Text";

export interface BrandDraft {
  name?: string;
  description?: string;
  audience?: string;
  primary?: string;
  secondary?: string;
  bg?: string;
  text?: string;
  accents?: string;
  headingFont?: string;
  bodyFont?: string;
  tone?: string;
}

/** Map the extraction draft (loose brand_kit dict) onto form values. */
function toDraft(res: BrandExtractionResponse): BrandDraft {
  const palette = (res.brandKit?.palette ?? {}) as Record<string, unknown>;
  const fonts = (res.brandKit?.fonts ?? {}) as Record<string, unknown>;
  return {
    name: res.name ?? undefined,
    description: res.description ?? undefined,
    audience: res.audience ?? undefined,
    primary: (palette.primary as string) ?? undefined,
    secondary: (palette.secondary as string) ?? undefined,
    bg: (palette.background as string) ?? undefined,
    text: (palette.text as string) ?? undefined,
    accents: Array.isArray(palette.accents) ? palette.accents.join(", ") : undefined,
    headingFont: (fonts.heading as string) ?? undefined,
    bodyFont: (fonts.body as string) ?? undefined,
    tone: (res.brandKit?.tone as string) ?? undefined,
  };
}

/** Inline "extract a brand kit from a guidelines doc" card on the create page. */
export function BrandExtractCard({ onDraft }: Readonly<{ onDraft: (draft: BrandDraft) => void }>) {
  const { t } = useTranslation("brands");
  const toast = useToast();
  const inputRef = useRef<HTMLInputElement>(null);

  const extract = useMutation({
    ...extractBrandMutation(),
    onSuccess: (res) => {
      onDraft(toDraft(res.data));
      toast.success(t("extract.applied"));
    },
    onError: (error) => toast.error(errorMessage(error, t("extract.failed"))),
  });

  return (
    <Card>
      <div className="flex items-start gap-3">
        <IconTile variant="brand">
          <FileText size={18} aria-hidden />
        </IconTile>
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <Text variant="body">{t("extract.title")}</Text>
          <Text variant="body-sm" tone="secondary">
            {t("extract.body")}
          </Text>
        </div>
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          className="flex shrink-0 cursor-pointer items-center gap-2 rounded-lg border border-border-input px-3 py-2 text-content-primary transition-colors hover:border-content-tertiary"
        >
          {extract.isPending ? <Spinner size="sm" /> : <Upload size={16} aria-hidden />}
          <Text variant="body-sm">{extract.isPending ? t("extract.analyzing") : t("extract.choose")}</Text>
        </button>
      </div>
      <input
        ref={inputRef}
        type="file"
        accept=".pdf,.docx,.pptx"
        className="hidden"
        aria-hidden
        tabIndex={-1}
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) extract.mutate({ body: { file: f } });
          e.target.value = "";
        }}
      />
    </Card>
  );
}
